// Segmented "None / Static / Dynamic" control for the WorkflowTab toolbar.
// Persists the chosen mode on section.workflow.scoringMode and, when flipping
// to "dynamic", auto-provisions the built-in `score` number variable so the
// runtime has somewhere to accumulate per-alternative + terminal points.
//
// Kept compact — three buttons in a row — to match the existing toolbar btn
// style. Disabled when there is no resolved section (course root with no
// scenes selected). The bridge between this toggle and the BranchingQuestion/
// EndScreen scoring fields lives in the visor; this component only persists
// the editor-side intent.
import React from "react";
import i18n from "i18next";
import { connect } from "react-redux";
import { setWorkflowScoringMode, ensureScoreVariable } from "../../../common/actions";
import { selectSectionWorkflow } from "./selectors/selectSectionWorkflow";
import "./ScoringModeToggle.scss";

const MODES = [
  { id: "none", labelKey: "Workflow.scoring_none", labelDefault: "None" },
  { id: "static", labelKey: "Workflow.scoring_static", labelDefault: "Static" },
  { id: "dynamic", labelKey: "Workflow.scoring_dynamic", labelDefault: "Dynamic" },
];

export function ScoringModeToggle({ sectionId, scoringMode, onSetMode, onEnsureScoreVar }) {
  const hasSection = sectionId !== null && sectionId !== undefined;
  const current = scoringMode || "none";

  const handleClick = (mode) => {
    if (!hasSection || mode === current) return;
    onSetMode(sectionId, mode);
    if (mode === "dynamic") {
      // Idempotent — the thunk bails if `score` already exists.
      onEnsureScoreVar(sectionId);
    }
  };

  return (
    <div
      className="scoring-mode-toggle"
      role="group"
      aria-label={i18n.t("Workflow.scoring_mode", { defaultValue: "Scoring mode" })}
      data-testid="scoring-mode-toggle"
    >
      <span className="scoring-mode-toggle__label">
        {i18n.t("Workflow.scoring_mode", { defaultValue: "Scoring" })}:
      </span>
      {MODES.map((m) => {
        const active = current === m.id;
        return (
          <button
            key={m.id}
            type="button"
            className={`scoring-mode-toggle__btn${active ? " scoring-mode-toggle__btn--active" : ""}`}
            onClick={() => handleClick(m.id)}
            disabled={!hasSection}
            aria-pressed={active}
            data-testid={`scoring-mode-toggle__btn-${m.id}`}
          >
            {i18n.t(m.labelKey, { defaultValue: m.labelDefault })}
          </button>
        );
      })}
    </div>
  );
}

const mapStateToProps = (state) => {
  const { sectionId, scoringMode } = selectSectionWorkflow(state);
  return { sectionId, scoringMode };
};

const mapDispatchToProps = {
  onSetMode: setWorkflowScoringMode,
  onEnsureScoreVar: ensureScoreVariable,
};

export default connect(mapStateToProps, mapDispatchToProps)(ScoringModeToggle);
